import { useEffect, useState } from "react";

import { ChevronLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";

import { usePageTitle } from "@rook/shared/lib/usePageTitle";

import {
  legalDocumentSourcePath,
  LEGAL_DOCUMENTS,
  lookupLegalDocument,
  type LegalDocumentMeta,
} from "./legalDocuments";

/**
 * Renders any of the legal documents under `/legal/<slug>`. Fetches
 * the raw markdown from `public/legal/<slug>.md` at runtime,
 * renders with `react-markdown` + `remark-gfm` (tables, autolinks,
 * task lists, strikethrough). Header shows version + lastUpdated
 * so a customer or auditor always knows which revision they're
 * looking at.
 *
 * Routing: this view is mounted at `/legal/:slug`. Unknown slugs
 * render the index (list of all docs). The route is public — no
 * auth gate — because customers + auditors + their legal counsel
 * need to read these without a session.
 *
 * Title: `usePageTitle("Privacy Policy · Legal · ROOK")` so the
 * browser tab + share-card meta tags read correctly.
 *
 * Why same-origin markdown fetch (not bundled at build time):
 *   - Updating a doc is a 1-file commit + cache-bust via the
 *     filename hash Vite generates. No JS bundle rebuild.
 *   - Lazy-loaded — the docs aren't downloaded unless the customer
 *     opens that route.
 *   - The same source feeds future PDF generation (one MD → many
 *     formats).
 */

const FetchStatus = {
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  NOT_FOUND: "not-found",
  ERROR: "error",
} as const;
type FetchStatusValue = (typeof FetchStatus)[keyof typeof FetchStatus];

const LegalDocumentIndex = () => {
  usePageTitle("Legal · ROOK");
  return (
    <main className="mx-auto max-w-3xl px-5 py-10">
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">
        Legal documents
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Every contract and policy ROOK ships with the Clients Portal,
        versioned and auditable.
      </p>
      <ul className="mt-6 space-y-3">
        {LEGAL_DOCUMENTS.map((doc) => (
          <li key={doc.slug}>
            <Link
              to={`/legal/${doc.slug}`}
              className="block rounded-lg border border-white/10 bg-card/50 px-4 py-3 transition-colors hover:bg-card hover:border-white/20"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-base font-medium text-foreground">
                  {doc.title}
                </span>
                <span className="text-xs text-muted-foreground">
                  v{doc.version} · {doc.lastUpdated}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {doc.description}
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
};

const DocumentHeader = ({ doc }: { doc: LegalDocumentMeta }) => (
  <header className="mb-8 border-b border-white/10 pb-6">
    <Link
      to="/legal"
      className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
    >
      <ChevronLeft className="h-3 w-3" aria-hidden />
      All legal documents
    </Link>
    <h1 className="mt-3 text-2xl font-semibold tracking-tight text-foreground">
      {doc.title}
    </h1>
    <p className="mt-1 text-sm text-muted-foreground">{doc.description}</p>
    <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
      <div className="flex gap-1.5">
        <dt className="font-semibold uppercase tracking-wider">Version</dt>
        <dd>{doc.version}</dd>
      </div>
      <div className="flex gap-1.5">
        <dt className="font-semibold uppercase tracking-wider">Updated</dt>
        <dd>{doc.lastUpdated}</dd>
      </div>
      <div className="flex gap-1.5">
        <dt className="font-semibold uppercase tracking-wider">Scope</dt>
        <dd>{doc.scope}</dd>
      </div>
    </dl>
  </header>
);

const NotFound = ({ slug }: { slug: string }) => (
  <div className="mx-auto max-w-3xl px-5 py-10">
    <p className="text-sm text-muted-foreground">
      No legal document at <code className="rounded bg-white/10 px-1 py-0.5">/legal/{slug}</code>.
    </p>
    <Link
      to="/legal"
      className="mt-3 inline-block text-sm text-primary hover:underline"
    >
      Browse all legal documents →
    </Link>
  </div>
);

export const LegalDocumentView = () => {
  const { slug } = useParams<{ slug?: string }>();
  // Resolve before any hook so the hooks below see a stable value
  // per render. Conditional returns must happen AFTER all hooks
  // (Rules of Hooks) — early-returning before useState/useEffect
  // would change the hook count when the user navigates between
  // /legal (no slug) and /legal/:slug.
  const doc = slug ? lookupLegalDocument(slug) : null;

  usePageTitle(doc ? `${doc.title} · Legal · ROOK` : "Legal · ROOK");

  const [status, setStatus] = useState<FetchStatusValue>(FetchStatus.IDLE);
  const [markdown, setMarkdown] = useState("");

  useEffect(() => {
    // Index or unknown-slug renders never fetch; clear stale state
    // so a back-nav from a real doc to the index doesn't flash old
    // content.
    if (!doc) {
      setStatus(FetchStatus.IDLE);
      setMarkdown("");
      return;
    }
    let cancelled = false;
    setStatus(FetchStatus.LOADING);
    fetch(legalDocumentSourcePath(doc.slug))
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (cancelled) return;
        // Defense in depth — if Amplify's SPA-fallback regex ever
        // catches `.md` (extension list drift, CDN cache lag during
        // the cutover), the fetch returns `index.html` instead of
        // markdown. Render that as a clean error rather than
        // letting react-markdown render `<!doctype html>` as
        // visible text.
        if (/^\s*<!doctype html/i.test(text) || /^\s*<html/i.test(text)) {
          setStatus(FetchStatus.ERROR);
          return;
        }
        setMarkdown(text);
        setStatus(FetchStatus.READY);
      })
      .catch(() => {
        if (cancelled) return;
        setStatus(FetchStatus.ERROR);
      });
    return () => {
      cancelled = true;
    };
  }, [doc]);

  // No slug → render the index (list).
  if (!slug) return <LegalDocumentIndex />;
  // Slug present but unknown → 404.
  if (!doc) return <NotFound slug={slug} />;

  return (
    <main className="mx-auto max-w-3xl px-5 py-10">
      <DocumentHeader doc={doc} />
      {status === FetchStatus.LOADING && (
        <p className="text-sm text-muted-foreground">Loading document…</p>
      )}
      {status === FetchStatus.ERROR && (
        <p className="text-sm text-destructive">
          Failed to load the document. Please refresh or contact
          support@tryrook.io.
        </p>
      )}
      {status === FetchStatus.READY && (
        <article
          className="prose prose-invert prose-headings:font-semibold prose-headings:tracking-tight prose-h1:text-2xl prose-h2:text-xl prose-h2:mt-8 prose-h3:text-base prose-h3:mt-6 prose-p:text-sm prose-li:text-sm prose-strong:text-foreground prose-a:text-primary max-w-none"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
        </article>
      )}
    </main>
  );
};
